import React, { useState, useMemo, useEffect } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  LabelList,
} from "recharts";
import { Paper, Typography, Box, IconButton, Divider } from "@mui/material";

import rocky from "./assets/rock.png";
import windows from "./assets/windows.png";
import linux from "./assets/linux.png";
import other from "./assets/other.png";
import ami from "./assets/aim.png";
import eks from "./assets/eks.png";
import ubuntu from "./assets/ubuntu.png";
import { WidgetContainer } from "../widgetContainer/widgetContainer";
import { DownloadExcel } from "../../../functions/downloadExcel";
import { format, parseISO } from "date-fns";
import { useTheme } from "@mui/material/styles";

const BarChartGeneric = ({
  data,
  interval = 20000,
  title,
  description = "",
  showBarIcon = false,
  showCount = false,
  insights = false,
  columnColorMapping = [],
  draggable = false,
  editMode = false,
  onBarClick = () => {},
  onInsights = () => {},
  loading = true,
}) => {
  const theme = useTheme();
  const chartTitle = data?.title || title;
  const envMetrics = data?.metrics || {};
  const envList = data?.environments || [];

  // We'll allow "ALL" plus the actual environments
  const allEnvs = ["ALL", ...envList];

  // State for active environment; default to "ALL"
  const [activeEnv, setActiveEnv] = useState("ALL");
  const [lastUpdatedDate, setLastUpdatedDate] = useState("");

  // Function to aggregate data from all environments if "ALL" is selected
  const aggregateAll = (metricsObj) => {
    const aggregated = {};
    for (const envKey of Object.keys(metricsObj)) {
      // each envKey is something like "OPS", "DEV"
      const envArray = metricsObj[envKey] || [];
      envArray.forEach(({ name, value }) => {
        aggregated[name] = (aggregated[name] || 0) + value;
      });
    }
    // convert aggregated object to array
    return Object.entries(aggregated).map(([name, value]) => ({ name, value }));
  };

  // Compute the barData for the currently active environment
  const barData = useMemo(() => {
    if (activeEnv === "ALL") {
      return aggregateAll(envMetrics);
    }
    // Return the environment-specific array, or empty if missing
    return envMetrics[activeEnv] || [];
  }, [activeEnv, envMetrics]);

  // Use the same color generation logic as lineGraph
  const getDistinctColor = (key, index = 0) => {
    const goldenAngle = 137.508;
    const hue = (index * goldenAngle) % 360;
    return `hsl(${hue}, 65%, 55%)`;
  };

  // Generate consistent color based on data name hash
  const hashStringToIndex = (str) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    return Math.abs(hash);
  };

  const getColorForDataName = (name) => {
    const index = hashStringToIndex(name.toLowerCase());
    return getDistinctColor(name, index);
  };

  useEffect(() => {
    const timestamp = data?.timestamp;

    if (timestamp) {
      const date = parseISO(timestamp);
      const day = parseInt(format(date, "d"), 10);
      const ordinal = (day) => {
        if (day >= 11 && day <= 13) return `${day}th`;
        switch (day % 10) {
          case 1:
            return `${day}st`;
          case 2:
            return `${day}nd`;
          case 3:
            return `${day}rd`;
          default:
            return `${day}th`;
        }
      };
      const formatted = `${format(date, "h:mm a EEE")} ${ordinal(day)} of ${format(date, "MMMM")}`;
      setLastUpdatedDate(formatted);
    }
  }, [data]);

  useEffect(() => {
    const cycleEnvironments = () => {
      setActiveEnv((prevEnv) => {
        const currentIndex = allEnvs.indexOf(prevEnv);
        const nextIndex = (currentIndex + 1) % allEnvs.length;
        return allEnvs[nextIndex];
      });
    };

    const intervalId = setInterval(cycleEnvironments, interval);
    return () => clearInterval(intervalId);
  }, [allEnvs, interval]);

  const totalCount = useMemo(() => {
    return barData.reduce((acc, curr) => acc + curr.value, 0);
  }, [barData]);

  const renderIconLabel = (props) => {
    const { x, y, width, height, value, name } = props;

    const image = (value) => {
      if (name.toLowerCase().indexOf("win") > -1) {
        return windows;
      } else if (name.toLowerCase().indexOf("rocky") > -1) {
        return rocky;
      } else if (name.toLowerCase().indexOf("linux") > -1) {
        return linux;
      } else if (name.toLowerCase().indexOf("ami") > -1) {
        return ami;
      } else if (name.toLowerCase().indexOf("eks") > -1) {
        return eks;
      } else if (name.toLowerCase().indexOf("ubuntu") > -1) {
        return ubuntu;
      } else {
        return other;
      }
    };

    return (
      <foreignObject
        x={x + width / 2 - 13}
        y={height < 20 ? y - 50 : y + height / 2 - 10}
        width="25"
        height="25"
      >
        <img src={image(value)} alt="icon" width="25" height="25" />
      </foreignObject>
    );
  };

  const CustomBar = (props) => {
    const { x, y, width, height, payload, fill, onBarClick } = props;

    const minHeight = 0; // Ensure bars have at least this height
    const adjustedHeight = Math.max(height, minHeight);
    const adjustedY = height < minHeight ? y - (minHeight - height) : y;

    return (
      <g onClick={() => onBarClick([payload, activeEnv])} style={{ cursor: "pointer" }}>
        {/* Actual Bar */}
        <rect x={x} y={adjustedY} width={width} height={adjustedHeight} fill={fill} />

        {/* Invisible Clickable Area */}
        <rect x={x} y={adjustedY - 500} width={width} height={"500px"} fill="transparent" />
      </g>
    );
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || payload.length === 0) return null;

    return (
      <Box
        sx={{
          backgroundColor: theme.palette.background.accent,
          border: "1px solid #444",
          borderRadius: "6px",
          padding: "10px 12px",
          color: theme.palette.text.primary,
          fontSize: 12,
          minWidth: "160px",
          opacity: 0.9,
        }}
      >
        <Typography
          sx={{
            fontWeight: "bold",
            fontSize: 13,
            textTransform: "uppercase",
          }}
        >
          {label}
        </Typography>

        <Divider sx={{ my: 1, borderColor: "#555" }} />

        {payload.map((entry, index) => (
          <Box
            key={`item-${index}`}
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            sx={{ fontSize: 12, mt: 0.5 }}
          >
            {entry && entry.name !== "value" && (
              <Box display="flex" alignItems="center" sx={{ mr: 5 }}>
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    backgroundColor: entry.color,
                    marginRight: 1,
                  }}
                />
                <span>{entry.name}</span>
              </Box>
            )}
            <span>{entry.value}</span>
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <WidgetContainer
      title={chartTitle}
      updatedAt={lastUpdatedDate}
      description={description}
      loaded={!loading}
      maximize={true}
      download={true}
      draggable={draggable}
      editMode={editMode}
      insights={insights}
      onInsights={onInsights}
      onDownload={() => {
        DownloadExcel(chartTitle, data);
      }}
    >
      {/* Main chart area */}
      <Box
        sx={{
          width: "100%",
          height: "90%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
          flex: 1,
          flexDirection: "column",
        }}
      >
        {barData.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={barData} margin={{ top: 10, right: 0, bottom: 15, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#777" />
              <XAxis
                dataKey="name"
                stroke="#fff"
                tickFormatter={(label) => label.toUpperCase()}
                tick={{
                  fill: theme.palette.text.primary,
                  fontSize: 12,
                  fontWeight: "bold",
                }}
                tickMargin={10}
              />
              <YAxis
                stroke="#fff"
                tick={{ fill: theme.palette.text.primary, fontWeight: "bold" }}
              />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: "rgba(255,255,255,0.05)" }} />

              {/* Single Bar: you could add multiple if you have multiple keys */}
              <Bar
                dataKey="value"
                shape={(props) => <CustomBar {...props} onBarClick={onBarClick} />}
              >
                {barData.map((entry, index) => (
                  <Cell
                    onClick={() => onBarClick(entry)}
                    key={`cell-${index}`}
                    fill={
                      (columnColorMapping.find((match) => match.name === entry.name) &&
                        columnColorMapping.find((match) => match.name === entry.name).color) ||
                      getColorForDataName(entry.name)
                    }
                  />
                ))}
                {/* LabelList for count values */}
                <LabelList
                  dataKey="value"
                  position="top"
                  fill={theme.palette.text.primary}
                  fontSize={14}
                  fontWeight="bold"
                />
                {showBarIcon && <LabelList dataKey="value" content={renderIconLabel} />}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        ) : (
          !loading && (
            <Box
              sx={{
                display: "flex",
                flex: 1,
                justifyContent: "center",
                alignItems: "center",
                width: "100%",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                  flex: 1,
                  width: "100%",
                  backgroundColor: theme.palette.background.default,
                  color: theme.palette.text.primary,
                  fontSize: "16px",
                  fontWeight: "bold",
                  padding: "20px",
                }}
              >
                No data available
              </Box>
            </Box>
          )
        )}
      </Box>

      {/* Bottom row: environment selector buttons (like your Pie chart) */}
      {barData.length > 0 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            gap: 1,

            flexWrap: "wrap",
          }}
        >
          {allEnvs.length > 0 ? (
            allEnvs.map((env, index) => (
              <IconButton
                key={env}
                size="small"
                onClick={() => setActiveEnv(env)}
                sx={{
                  borderRadius: 2,
                  fontSize: 10,
                  fontWeight: "bold",
                  // backgroundColor: activeEnv === env ? "rgba(255, 255, 255, 0.1)" : "transparent",
                  // color: activeEnv === env ? theme.palette.text.primary : theme.palette.text.secondary,

                  backgroundColor:
                    activeEnv === env
                      ? env === "ALL"
                        ? "#ccc"
                        : getDistinctColor(env, index)
                      : "transparent",
                  color:
                    activeEnv === env
                      ? env === "ALL"
                        ? "#000"
                        : theme.palette.text.invert
                      : theme.palette.text.primary,

                  border: "1px solid rgba(255, 255, 255, 0.2)",
                  boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.2)",
                  "&:hover": {
                    backgroundColor:
                      activeEnv === env ? getDistinctColor(env, index) : "rgba(255, 255, 255, 0.2)",
                    boxShadow: "0px 3px 6px rgba(0, 0, 0, 0.3)",
                  },
                }}
              >
                {env && env.toUpperCase()}
              </IconButton>
            ))
          ) : (
            <Typography sx={{ color: "#fff" }}>No environment data.</Typography>
          )}
        </Box>
      )}
    </WidgetContainer>
  );
};

export default React.memo(BarChartGeneric);
